import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Divider, Tooltip as MuiTooltip, TooltipProps, useTheme } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';

type InfoTooltipProps = {
  title: string;
  description: string | React.ReactNode;
  metaHint?: string;
  icon?: React.ReactNode;
  placement?: TooltipProps['placement'];
};

export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  title,
  description,
  metaHint,
  icon,
  placement = 'right',
}) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (anchorRef.current && !anchorRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  const tooltipContent = (
    <Box sx={{
      maxWidth: 320,
      p: 2,
      backgroundColor: 'background.paper',
      border: '1px solid',
      borderColor: 'rgba(123, 97, 255, 0.3)',
      borderRadius: 3,
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    }}>
      {/* Header with icon and title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        {icon && (
          <Box sx={{ 
            fontSize: '1.1rem',
            opacity: 0.7,
            display: 'flex',
            alignItems: 'center',
            color: isLight ? 'primary.main' : 'text.secondary',
            '& svg': {
              color: isLight ? 'primary.main' : 'text.secondary',
            },
          }}>
            {icon}
          </Box>
        )}
        <Typography variant="subtitle2" sx={{
          fontWeight: 600,
          color: 'text.primary',
          fontSize: (theme: any) => theme.typography.fontSizes.body,
        }}>
          {title}
        </Typography>
      </Box>

      {/* Description */}
      <Typography variant="body2" sx={{
        color: 'text.secondary',
        fontSize: (theme: any) => theme.typography.fontSizes.body,
        lineHeight: 1.5,
        mb: metaHint ? 1.5 : 0,
      }}>
        {description}
      </Typography>

      {/* Optional divider and meta hint */}
      {metaHint && (
        <>
          <Divider sx={{ my: 1.5, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          <Typography variant="caption" sx={{
            color: 'text.secondary',
            fontSize: (theme: any) => theme.typography.fontSizes.small,
            opacity: 0.7,
            fontStyle: 'italic',
          }}>
            {metaHint}
          </Typography>
        </>
      )}
    </Box>
  );

  return (
    <Box
      ref={anchorRef}
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        ml: 0.5,
        verticalAlign: 'middle',
      }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onClick={(e) => {
        e.stopPropagation();
        setOpen(!open);
      }}
    >
      <MuiTooltip
        open={open}
        title={tooltipContent}
        placement={placement}
        arrow
        disableHoverListener
        disableFocusListener
        disableTouchListener
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: 'transparent',
              padding: 0,
              maxWidth: 'none',
              boxShadow: 'none',
            },
          },
          arrow: {
            sx: {
              color: 'rgba(123, 97, 255, 0.3)',
            },
          },
        }}
      >
        <InfoOutlined
          sx={{
            fontSize: '1rem',
            color: isLight ? 'rgba(0, 0, 0, 0.45)' : 'rgba(255, 255, 255, 0.45)',
            cursor: 'help',
            transition: 'all 0.2s ease',
            '&:hover': {
              color: 'primary.main',
              filter: 'drop-shadow(0 0 4px rgba(123, 97, 255, 0.4))',
            },
          }}
        />
      </MuiTooltip>
    </Box>
  );
};

